import { useState } from 'react'
import { createPortal } from 'react-dom'
import { api } from '../../api'
import { ORANGE, TEAL, VERT, fmtDateLongue } from './marcheProducer'

/**
 * Facture de commission affichee PLEIN ECRAN (proto v7.2.3, style
 * `manrina-facturation-v3` valide et deploye au BO) : en-tete vert degrade
 * (n° COM Instrument Serif + badge statut), emetteur/destinataire, base CA,
 * commission HT / TVA 8,5 % / TTC (montants serif), separateur PERFORE
 * « ticket de marche », bloc reglement teal, pied charte, bouton PDF orange.
 * Donnees 100 % reelles : snapshot ModuleInvoice (jamais recalcule ici).
 */

const eurF = (n) => `${Number(n || 0).toFixed(2).replace('.', ',')} €`

// Telechargement compatible Safari/iOS (CONVENTIONS : pas de window.open direct)
const telechargerBlob = (blob, filename) => {
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = filename
  document.body.appendChild(a)
  a.click()
  document.body.removeChild(a)
  setTimeout(() => URL.revokeObjectURL(url), 4000)
}

function Perfo() {
  return (
    <div className="relative" style={{ height: 22, margin: '0 -18px' }}>
      <div className="absolute" style={{ left: 26, right: 26, top: '50%', borderTop: '2px dashed rgba(7,62,61,0.18)' }} />
      <span className="absolute rounded-full" style={{ left: -11, top: 0, width: 22, height: 22, background: 'rgba(4,25,24,0.55)' }} />
      <span className="absolute rounded-full" style={{ right: -11, top: 0, width: 22, height: 22, background: 'rgba(4,25,24,0.55)' }} />
    </div>
  )
}

function FactureComModal({ facture, eventNom, eventDate, enAttenteManrina, onClose, onToast }) {
  const [pdfLoading, setPdfLoading] = useState(false)
  if (!facture) return null

  const regle = facture.statut === 'PAYEE'
  const badge = regle
    ? { l: '● Réglée', c: '#7ee8d8' }
    : enAttenteManrina
      ? { l: '● En attente Manrina', c: '#ffc9a3' }
      : { l: '● À régler', c: '#ffb3a3' }
  const modeLbl = {
    AVOIR: 'votre solde wallet',
    CARTE_EN_LIGNE: 'carte bancaire',
    CB: 'carte bancaire',
    VIREMENT: 'virement',
    ESPECES: 'espèces',
  }[facture.modePaiement] || null

  const pdf = async () => {
    setPdfLoading(true)
    try {
      const blob = await api.producer.downloadModuleInvoicePdf(facture.id)
      telechargerBlob(blob, `${facture.numero}.pdf`)
    } catch (e) {
      onToast?.(e.message || 'Le téléchargement du PDF a échoué.')
    } finally {
      setPdfLoading(false)
    }
  }

  return createPortal(
    <div
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      className="fixed inset-0 z-[70] flex items-center justify-center"
      style={{ background: 'rgba(4,25,24,0.55)', backdropFilter: 'blur(10px)', WebkitBackdropFilter: 'blur(10px)', padding: '20px 14px' }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="w-full overflow-y-auto overscroll-contain"
        style={{ maxWidth: 420, maxHeight: '88dvh', borderRadius: 24, background: '#fdf9f4', boxShadow: '0 24px 70px rgba(0,0,0,0.35)' }}
      >
        {/* En-tete vert degrade */}
        <div className="relative overflow-hidden" style={{ background: 'linear-gradient(140deg, rgb(var(--c-green)), #0a5a54)', padding: '20px 18px 18px', color: 'rgb(var(--c-creme))' }}>
          <div className="absolute rounded-full pointer-events-none" style={{ top: -50, right: -40, width: 160, height: 160, background: 'radial-gradient(circle, rgba(20,184,166,0.35), transparent 70%)' }} />
          <div className="relative flex justify-between items-start">
            <div>
              <p className="text-[10px] font-bold uppercase" style={{ letterSpacing: 2.5, opacity: 0.7 }}>Facture de commission</p>
              <p className="font-serif text-[26px] mt-0.5">{facture.numero}</p>
              <p className="text-[11.5px] mt-1" style={{ opacity: 0.75 }}>
                {eventNom}{eventDate ? ` · ${fmtDateLongue(eventDate)}` : ''}
              </p>
            </div>
            <span className="rounded-full text-[11px] font-extrabold" style={{ padding: '5px 11px', background: 'rgba(255,255,255,0.14)', color: badge.c }}>
              {badge.l}
            </span>
          </div>
        </div>

        <div style={{ padding: '16px 18px 20px' }}>
          {/* Emetteur / destinataire */}
          <div className="flex gap-2.5 mb-3.5">
            {[
              ['Émetteur', facture.emetteurNom || 'Manrina', 'Martinique'],
              ['Destinataire', facture.destinataireNom || '—', facture.destinataireAdresse || ''],
            ].map(([t, n, l]) => (
              <div key={t} className="flex-1 rounded-[14px]" style={{ background: 'rgba(255,255,255,0.7)', border: '1px solid rgba(7,62,61,0.08)', padding: '10px 12px' }}>
                <p className="text-[9.5px] font-extrabold uppercase" style={{ letterSpacing: 1.5, color: 'rgba(7,62,61,0.45)' }}>{t}</p>
                <p className="text-[12.5px] font-bold mt-0.5" style={{ color: VERT }}>{n}</p>
                <p className="text-[10.5px] truncate text-ink/50">{l}</p>
              </div>
            ))}
          </div>

          {/* Base + detail commission (snapshot facture — TVA en dedans) */}
          <div className="rounded-2xl" style={{ background: 'rgba(255,255,255,0.7)', border: '1px solid rgba(7,62,61,0.08)', padding: '13px 15px' }}>
            <div className="flex justify-between py-1" style={{ borderBottom: '1px solid rgba(7,62,61,0.07)' }}>
              <span className="text-xs text-ink/60">Base : CA du marché (encaissé par vous)</span>
              <span className="font-serif text-[13px]" style={{ color: VERT }}>{eurF(facture.grossSalesBase)}</span>
            </div>
            {[
              [`Commission Manrina (${Number(facture.commissionRate) || ''} %) HT`, facture.montantHT],
              ['TVA 8,5 % (sur la commission seule)', facture.montantTVA],
            ].map(([l, v]) => (
              <div key={l} className="flex justify-between py-[5px]">
                <span className="text-xs text-ink/65">{l}</span>
                <span className="font-serif text-[13px]" style={{ color: VERT }}>{eurF(v)}</span>
              </div>
            ))}
            <div className="flex justify-between pt-2 mt-1" style={{ borderTop: '1.5px solid rgba(7,62,61,0.14)' }}>
              <span className="text-[13.5px] font-extrabold" style={{ color: VERT }}>Total TTC</span>
              <span className="font-serif text-lg font-bold" style={{ color: ORANGE }}>{eurF(facture.montantTTC)}</span>
            </div>
          </div>

          <Perfo />

          {/* Bloc reglement */}
          <div className="rounded-[14px]" style={{ background: 'rgba(20,184,166,0.09)', border: '1px solid rgba(20,184,166,0.25)', padding: '11px 13px' }}>
            <p className="text-xs font-bold leading-relaxed" style={{ color: '#0d6b5f' }}>
              {regle
                ? `Réglée par ${modeLbl || '—'}. Votre CA reste intégralement à vous — seule la commission est facturée.`
                : enAttenteManrina
                  ? `Règlement par ${modeLbl || '—'} déclaré — en attente de confirmation par Manrina.`
                  : 'En attente de règlement. Réglez depuis Marché → Historique (wallet, carte, virement ou espèces).'}
            </p>
          </div>

          {/* Pied charte + PDF */}
          <p className="text-[10.5px] leading-relaxed text-ink/50" style={{ margin: '12px 2px' }}>
            La commission correspond au coût réel du service rendu, conformément à la charte Manrina. Le prix que vous
            fixez est sanctuarisé.
          </p>
          <button
            onClick={pdf}
            disabled={pdfLoading}
            className="w-full rounded-2xl text-sm font-extrabold text-white disabled:opacity-60"
            style={{ padding: 14, border: 'none', background: `linear-gradient(135deg, ${ORANGE}, #e8703a)`, boxShadow: '0 8px 24px rgba(244,137,83,0.4)' }}
          >
            {pdfLoading ? 'Préparation…' : '📄 Télécharger le PDF'}
          </button>
          <button
            onClick={onClose}
            className="w-full mt-2 rounded-2xl text-[13px] font-bold"
            style={{ padding: 12, border: 'none', background: 'rgba(7,62,61,0.06)', color: VERT }}
          >
            Fermer
          </button>
        </div>
      </div>
    </div>,
    document.body,
  )
}

export default FactureComModal
